import type { CityConfig } from "../city-template/types";

export const lafayetteCoConfig: CityConfig = {
  city: "Lafayette",
  state: "Colorado",
  stateAbbr: "CO",
  slug: "lafayette-co",
  route: "/cities/lafayette-co",
  title: "Emergency & Expedited Passport Lafayette CO | Same-Day Boulder County | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Lafayette, Colorado — as fast as 24 hours. Same-day courier processing for Lafayette, Louisville, Erie, Broomfield, and all Boulder County travelers. A+ BBB rated, registered State Dept. courier. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Lafayette, CO",
  heroSubtitle: "Family trip to Europe during school break and the kids' passports just expired? Good Samaritan Medical Center conference abroad with last-minute travel documents needed? We help Lafayette residents — from Old Town families to Waneka Lake neighbors to Indian Peaks professionals — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates typically 30–100% lower than other passport courier resellers. As fast as 24 hours. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lafayette%20Colorado%20historic%20Old%20Town%20district%20with%20charming%20shops%20and%20restaurants%20along%20Public%20Road%2C%20tree%20lined%20streets%20in%20a%20classic%20Colorado%20small%20town%20setting%2C%20the%20Rocky%20Mountain%20Front%20Range%20in%20the%20distance%2C%20Waneka%20Lake%20with%20green%20open%20space%20and%20walking%20paths%2C%20warm%20afternoon%20light%20casting%20long%20shadows%2C%20professional%20landscape%20photography%2C%20no%20visible%20text%20or%20people%2C%20clean%20wide%20angle%20view%20capturing%20small%20town%20charm%20with%20mountain%20backdrop&width=1800&height=700&seq=lafayette-co-hero-001&orientation=landscape",
  heroImageAlt: "Lafayette Colorado Old Town historic district with Front Range mountain backdrop and Waneka Lake",
  geoPosition: "39.9936;-105.0897",
  geoPlacename: "Lafayette, Colorado",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Denver Passport Agency",
    address: "3151 S. Vaughn Way, Aurora, CO 80014",
    distance: "Approximately 30 miles southeast via US-36 and I-225 — about 40 minutes",
  },
  nearbyCitiesZips: [
    { name: "Lafayette", zips: "80026" },
    { name: "Louisville", zips: "80027" },
    { name: "Erie", zips: "80514, 80516" },
    { name: "Broomfield", zips: "80020, 80021, 80023, 80038" },
    { name: "Boulder", zips: "80301, 80302, 80303, 80304, 80305" },
    { name: "Superior", zips: "80027" },
    { name: "Longmont", zips: "80501, 80502, 80503, 80504" },
    { name: "Westminster", zips: "80020, 80021, 80023, 80030, 80031" },
    { name: "Arvada", zips: "80001, 80002, 80003, 80004, 80005, 80007" },
    { name: "Thornton", zips: "80229, 80233, 80241, 80260, 80602" },
    { name: "Niwot", zips: "80503, 80544" },
    { name: "Gunbarrel", zips: "80301" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier service for urgent Boulder County departures and DIA international flights" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved — just 30 miles to the Denver Passport Agency" },
    { icon: "ri-heart-pulse-line", title: "Healthcare Professional Service", desc: "Specialized service for Good Samaritan Medical Center staff with international conferences" },
    { icon: "ri-mail-send-line", title: "Overnight Shipping", desc: "Prepaid labels from any UPS or FedEx in Lafayette, Louisville, or Boulder" },
  ],
  internalLinks: [
    { name: "Expedited passport in Boulder, CO", route: "/cities/boulder" },
    { name: "Emergency passport help in Broomfield, CO", route: "/cities/broomfield" },
    { name: "Fast passport renewal in Louisville area", route: "/cities/lafayette-co" },
    { name: "Urgent passport services for Erie and Superior", route: "/cities/erie-co" },
    { name: "Colorado passport services overview", route: "/states/colorado" },
    { name: "Passport courier services in Longmont and Niwot", route: "/cities/longmont" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Lafayette remotely via secure registered courier. Our couriers hand-deliver your documents directly to U.S. Department of State processing facilities. Lafayette's location — 30 miles from the Denver Passport Agency — makes our courier service especially valuable. Skip the drive down US-36 and let us handle everything.",
  fpcOfficeAddress: "3151 S. Vaughn Way, Aurora, CO 80014",
  fpcOfficeDistance: "Serving all of Lafayette, Louisville, and Boulder County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Lafayette and the surrounding Boulder County communities. From Old Town Lafayette and the Public Road corridor to the Indian Peaks and Waneka Lake neighborhoods, from the Coal Creek Trail to the Baseline corridor — we serve every corner of Lafayette and the surrounding area.",
  nearbyCitiesIntro: "We serve Lafayette and all nearby Boulder County communities including Louisville, Erie, Broomfield, Boulder, Superior, Longmont, Niwot, and Gunbarrel. We also serve Westminster, Arvada, and Thornton in the north metro area.",
  howItWorksIntro: "Most Lafayette travelers complete everything online and ship documents via overnight courier. Our registered couriers handle the State Department delivery — no need to drive 30 miles to Denver on US-36.",
  mistakesIntro: "Lafayette combines small-town charm with proximity to Boulder and Denver. Many residents travel internationally for both business and pleasure. Here are the most common passport mistakes we see from Lafayette-area travelers.",
  testimonialsIntro: "Lafayette travelers — from Old Town families to Indian Peaks professionals to Good Samaritan healthcare workers — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Lafayette, CO? Here are the most common ones we hear from Boulder County travelers.",
  statePageRoute: "/states/colorado",
  statePageName: "Colorado",
  dmvWebsite: "https://dmv.colorado.gov",
  dmvLabel: "Colorado DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Drive to Denver (40 min via US-36/I-225)", "Subject to availability — no guarantees"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "No Denver drive required — ship from Lafayette", "Perfect for family vacations and European getaways", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Faster than self-service", "Good for planned seasonal travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Lafayette travelers with immediate departure needs — as fast as 24 hours. Our couriers provide the fastest passport service in Boulder County.",
      bullets: ["24–48 hour turnaround available", "Healthcare conference and family travel expertise", "Boulder County and DIA departure support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Lafayette adults with a passport issued within the last 15 years. Skip the post office and let our couriers handle everything.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Lafayette or Boulder County address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Lafayette residents — including young professionals and new families.",
      bullets: ["Acceptance facility scheduling in Lafayette or Boulder", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Lafayette residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Lafayette's many families traveling internationally with children.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Lafayette and Boulder County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Lafayette CO", description: "Urgent passport processing for Lafayette Colorado travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Lafayette CO", description: "Fast passport renewal for Lafayette Colorado residents with a previously-issued passport." },
    { name: "First-Time Passport Lafayette CO", description: "New passport application services for Lafayette Colorado residents applying for their first passport." },
    { name: "Child Passport Lafayette CO", description: "Minor passport services for Lafayette Colorado families with children under 16." },
  ],
  testimonials: [
    {
      name: "Laura M.",
      location: "Lafayette, CO — Old Town",
      rating: 5,
      text: "Family trip to France over Christmas break and we had three expired passports. Fast Passport Center processed all three in 5 business days. The courier service meant we never left Lafayette. Our best family vacation yet.",
      service: "Family Passport Bundle",
    },
    {
      name: "Dr. Steven K.",
      location: "Lafayette, CO — Indian Peaks",
      rating: 5,
      text: "Medical conference in Vienna with 10 days notice. My passport was expiring soon and Austria requires 6 months validity. Fast Passport Center expedited my renewal in 3 days. Presented my research on schedule.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Nancy R.",
      location: "Louisville, CO — near Lafayette",
      rating: 5,
      text: "Retirement trip to Australia with my husband. Both passports needed renewal. Fast Passport Center handled everything in 5 days. The document review caught an issue with my photo that would have been rejected.",
      service: "Passport Renewal",
    },
    {
      name: "Jason T.",
      location: "Lafayette, CO — Waneka Lake",
      rating: 5,
      text: "Lost my passport during a hiking trip in Rocky Mountain National Park. Had a business trip to Hong Kong in 6 days. Fast Passport Center replaced it in 48 hours. Made the flight. Incredible.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "lafayette-co-1",
      name: "Lafayette Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "603 S Public Rd",
      city: "Lafayette",
      state: "CO",
      zip: "80026",
      phone: "(303) 665-0640",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "Central Lafayette, on South Public Road",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Lafayette acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient for Old Town and central Lafayette residents.",
      icon: "ri-mail-line",
    },
    {
      id: "lafayette-co-2",
      name: "Louisville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 W Pine St",
      city: "Louisville",
      state: "CO",
      zip: "80027",
      phone: "(303) 666-4271",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "Louisville, approximately 5 miles south of Lafayette",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Louisville and south Lafayette residents. Historic downtown Louisville location. Popular with families.",
      icon: "ri-mail-line",
    },
    {
      id: "lafayette-co-3",
      name: "Broomfield Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3001 W 124th Ave",
      city: "Broomfield",
      state: "CO",
      zip: "80020",
      phone: "(303) 469-0719",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "Broomfield, approximately 8 miles southeast of Lafayette",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest nearby facility with extended evening hours. Good for after-work appointments. Serves Broomfield and east Lafayette.",
      icon: "ri-mail-line",
    },
    {
      id: "lafayette-co-4",
      name: "Boulder Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1905 15th St",
      city: "Boulder",
      state: "CO",
      zip: "80302",
      phone: "(303) 443-6521",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "Boulder, approximately 10 miles west of Lafayette",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Large facility serving Boulder and surrounding communities. Extended hours and full passport services. Ideal for Lafayette residents who work in Boulder.",
      icon: "ri-mail-line",
    },
    {
      id: "lafayette-co-5",
      name: "Boulder County Clerk — Boulder",
      type: "County Clerk Passport Acceptance",
      address: "1750 33rd St",
      city: "Boulder",
      state: "CO",
      zip: "80301",
      phone: "(303) 413-7717",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Boulder, approximately 10 miles west of Lafayette",
      website: "https://www.bouldercounty.gov/",
      websiteLabel: "Boulder County Clerk",
      notes: "Boulder County clerk passport acceptance with early morning hours. Convenient for Lafayette residents who want a pre-work appointment.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 40 min drive to Denver via US-36/I-225", fpc: "No — ship from Lafayette" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "US-36 Traffic", agency: "Heavy commuter traffic — unpredictable", fpc: "No driving needed — courier handles it" },
  ],
  mistakes: [
    {
      icon: "ri-calendar-line",
      title: "Waiting Until Peak Travel Seasons",
      description: "Lafayette residents often book international travel during summer and winter holidays. These periods see peak passport demand. Start expedited service at least 4 weeks before your family trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo Background",
      description: "Local photo services sometimes use off-white or cream backgrounds. Passport photos require a pure white or off-white background with no shadows. Verify before submitting.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Checking Facility Hours",
      description: "The Lafayette Post Office has limited hours (closes at 4:30 PM). If you work in Boulder or Denver, you may need to use the Broomfield facility (open until 5:00 PM) or the Boulder Main Post Office.",
    },
    {
      icon: "ri-heart-pulse-line",
      title: "Healthcare Conference Passport Rush",
      description: "Good Samaritan Medical Center staff often attend international medical conferences. Verify your passport validity the moment you submit your conference abstract — don't wait until registration.",
    },
    {
      icon: "ri-parent-line",
      title: "Incomplete Child Passport Documentation",
      description: "For minors, both parents must appear at the acceptance facility with valid ID. Single parents or guardians need notarized consent from the absent parent. Many Lafayette families arrive unprepared.",
    },
    {
      icon: "ri-global-line",
      title: "Not Checking Destination Entry Requirements",
      description: "Many countries require 6 months of passport validity beyond your stay AND blank visa pages. Lafayette travelers to Europe and Asia often discover these requirements too late.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in Lafayette or office in Boulder." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx in Lafayette, Louisville, or Boulder." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — catching errors before they cause rejections." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier submits to State Department facilities — no US-36 drive to Denver required." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Lafayette address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Lafayette, CO?", a: "Fast Passport Center offers emergency passport processing in Lafayette, Colorado in as little as 24–48 hours. Our registered couriers hand-deliver your documents to U.S. Department of State facilities — no need to make the 30-mile drive on US-36 to the Denver Passport Agency." },
    { q: "Does Lafayette have its own passport agency?", a: "No — Lafayette does not have a regional passport agency. The nearest is the Denver Passport Agency (30 miles southeast via US-36 and I-225). Fast Passport Center's courier service means Lafayette residents never need to make the drive." },
    { q: "Can you help Good Samaritan Medical Center staff with passports?", a: "Yes. Fast Passport Center serves all Lafayette healthcare professionals including Good Samaritan Medical Center staff. We understand the demands of medical careers and can coordinate expedited passport services for international conferences and medical missions." },
    { q: "Do you serve Louisville and Superior too?", a: "Absolutely. Fast Passport Center serves all Boulder County communities including Louisville, Superior, Erie, Broomfield, Boulder, Longmont, Niwot, and Gunbarrel. Our service is identical across the entire area." },
    { q: "How much does an expedited passport cost in Lafayette, CO?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Lafayette?", a: "The UPS Store at 555 W South Boulder Rd in Lafayette offers passport photo services. Additional options include Walgreens in Boulder (2998 Baseline Rd), CVS in Boulder (1840 Folsom St), and King Soopers on Baseline. Photos must be 2×2 inches on a white background." },
    { q: "What's the closest major airport for international flights from Lafayette?", a: "Denver International Airport (DIA) is the primary international airport for Lafayette travelers — approximately 40 miles southeast via US-36 and E-470. DIA offers nonstop flights to Europe, Asia, Mexico, Canada, and domestic destinations. Allow 45–55 minutes for the drive." },
    { q: "Can you process passports for the whole family at once?", a: "Yes. Fast Passport Center specializes in family passport bundles. We can process multiple applications simultaneously — adults renewing, kids getting first-time passports, and everything in between. All coordinated to match your family travel timeline." },
  ],
  photoLocations: [
    { name: "UPS Store — Lafayette", address: "555 W South Boulder Rd, Lafayette, CO 80026", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–3PM" },
    { name: "Walgreens — Boulder (Baseline)", address: "2998 Baseline Rd, Boulder, CO 80303", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Boulder", address: "1840 Folsom St, Boulder, CO 80302", hours: "Mon–Sun: 8AM–9PM" },
    { name: "King Soopers — Boulder (Baseline)", address: "4800 Baseline Rd, Boulder, CO 80303", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    { name: "DMV — Boulder (Boulder County)", address: "2850 Iris Ave, Boulder, CO 80304", hours: "Mon–Fri: 8AM–5PM", note: "Full-service DMV for Boulder County residents. REAL ID and driver's license renewals. Most convenient for Lafayette residents.", featured: true },
    { name: "DMV — Broomfield (Broomfield County)", address: "1 Descombes Dr, Broomfield, CO 80020", hours: "Mon–Fri: 8AM–5PM", note: "Alternative DMV for Lafayette residents closer to Broomfield. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "DEN",
      name: "Denver International Airport",
      distance: "40 miles southeast of Lafayette via US-36 and E-470",
      address: "8500 Peña Blvd, Denver, CO 80249",
      note: "Colorado's primary international airport with nonstop flights to Europe, Asia, Mexico, Canada, and domestic destinations. Approximately 45–55 minutes from Lafayette.",
      flightUrl: "https://www.flydenver.com/",
      flightLabel: "DEN Flight Info",
      featured: true,
    },
    {
      code: "BDU",
      name: "Boulder Municipal Airport",
      distance: "12 miles west of Lafayette via Arapahoe Road",
      address: "3327 Airport Rd, Boulder, CO 80301",
      note: "General aviation airport serving Boulder and the surrounding area. No commercial flights. Convenient for private aviation travelers.",
      flightUrl: "https://bouldercolorado.gov/",
      flightLabel: "BDU Info",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates (CO CDPHE)", url: "https://cdphe.colorado.gov/vitalrecords", desc: "Colorado Department of Public Health & Environment for birth and death certificates statewide" },
    { label: "Marriage Licenses (Boulder County Clerk)", url: "https://www.bouldercounty.org/", desc: "Boulder County Clerk for Lafayette and Boulder County" },
    { label: "Divorce Decrees (Boulder County District Court)", url: "https://www.courts.state.co.us/Courts/District/Index.cfm?District_ID=20", desc: "Boulder County District Court for divorce decree records" },
    { label: "VitalChek Online Ordering", url: "https://www.vitalchek.com/", desc: "Order Colorado vital records online via VitalChek" },
  ],
};